import React, { useState } from 'react';
import { Language, Translations } from '../i18n/translations';
import { LogoMark } from './Logo';
import { SearchIcon, UserIcon, CartIcon, HamburgerIcon, CloseIcon } from './icons';
interface HeaderProps {
  lang: Language;
  setLang: (lang: Language) => void;
  t: Translations;
  setPage: (page: 'home' | 'product') => void;
}
export const Header: React.FC<HeaderProps> = ({
  lang,
  setLang,
  t,
  setPage
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [accessoriesOpen, setAccessoriesOpen] = useState(false);
  const [mobileAccessoriesOpen, setMobileAccessoriesOpen] = useState(false);
  const navLinks = [{
    label: t.nav.accessories,
    id: 'accessories',
    hasDropdown: true
  }, {
    label: t.nav.recovery,
    id: 'recovery'
  }, {
    label: t.nav.support,
    id: 'support'
  }, {
    label: t.nav.kits,
    id: 'kits'
  }];
  return <>
      {/* Top Bar */}
      <div className="sticky top-0 z-50 w-full h-9 bg-beige border-b border-gold/40 flex items-center justify-between px-4 lg:px-8 text-xs tracking-wide" data-id="element-70">
        <div className="text-ink/80" data-id="element-71">{t.topbar.freeShipping}</div>
        <div className="flex items-center space-x-2 font-medium" data-id="element-72">
          <button onClick={() => setLang('es')} className={`transition-soft ${lang === 'es' ? 'text-gold' : 'text-gold/40 hover:text-gold/70'}`} data-id="element-73">
            ES
          </button>
          <span className="text-gold/40" data-id="element-74">|</span>
          <button onClick={() => setLang('en')} className={`transition-soft ${lang === 'en' ? 'text-gold' : 'text-gold/40 hover:text-gold/70'}`} data-id="element-75">
            EN
          </button>
        </div>
      </div>

      {/* Main Header */}
      <header className="sticky top-9 z-40 w-full bg-cream border-b border-gold/20 py-4 px-4 lg:px-8 transition-soft" data-id="element-76">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-77">
          {/* Mobile Hamburger */}
          <button className="lg:hidden text-gold" onClick={() => setIsMobileMenuOpen(true)} data-id="element-78">
            <HamburgerIcon data-id="element-79" />
          </button>

          {/* Logo */}
          <button onClick={() => setPage('home')} className="flex items-center space-x-3 group" data-id="element-80">
            <LogoMark className="w-10 h-10 group-hover:scale-105 transition-soft" data-id="element-81" />
            <span className="font-serif text-xl lg:text-2xl tracking-wide text-gold" data-id="element-82">
              SCULPTED BODY
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-8" data-id="element-83">
            {navLinks.map(link => link.hasDropdown ? <div key={link.id} className="relative" onMouseEnter={() => setAccessoriesOpen(true)} onMouseLeave={() => setAccessoriesOpen(false)} data-id="element-84">
                  <button onClick={() => setAccessoriesOpen(v => !v)} className="text-sm uppercase tracking-wider text-ink/80 hover:text-gold relative group py-2 flex items-center space-x-1" data-id="element-85">
                    <span data-id="element-86">{link.label}</span>
                    <svg className={`w-3 h-3 transition-transform duration-200 ${accessoriesOpen ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-87">
                      <path d="M3 4.5L6 7.5L9 4.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-88" />
                    </svg>
                  </button>
                  <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-200 ${accessoriesOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'}`} data-id="element-89">
                    <div className="bg-cream border border-gold/20 rounded-sm shadow-lg min-w-[180px] py-2" data-id="element-90">
                      <button onClick={() => {
                  setPage('home');
                  setAccessoriesOpen(false);
                }} className="w-full text-left px-5 py-2.5 text-sm uppercase tracking-wider text-ink/80 hover:text-gold hover:bg-beige/50 transition-soft" data-id="element-91">
                        {t.nav.women}
                      </button>
                      <button onClick={() => {
                  setPage('home');
                  setAccessoriesOpen(false);
                }} className="w-full text-left px-5 py-2.5 text-sm uppercase tracking-wider text-ink/80 hover:text-gold hover:bg-beige/50 transition-soft" data-id="element-92">
                        {t.nav.men}
                      </button>
                    </div>
                  </div>
                </div> : <button key={link.id} onClick={() => setPage('home')} className="text-sm uppercase tracking-wider text-ink/80 hover:text-gold relative group py-2" data-id="element-93">
                  {link.label}
                  <span className="absolute bottom-0 left-0 w-full h-[1px] bg-gold scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" data-id="element-94"></span>
                </button>)}
          </nav>

          {/* Icons */}
          <div className="flex items-center space-x-4 lg:space-x-6 text-gold" data-id="element-95">
            <button className="hidden lg:block hover:scale-110 transition-soft" data-id="element-96">
              <SearchIcon data-id="element-97" />
            </button>
            <button className="hidden lg:block hover:scale-110 transition-soft" data-id="element-98">
              <UserIcon data-id="element-99" />
            </button>
            <button className="relative hover:scale-110 transition-soft" data-id="element-100">
              <CartIcon data-id="element-101" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-gold text-white text-[10px] flex items-center justify-center rounded-full font-medium" data-id="element-102">
                2
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <div className={`fixed inset-0 z-[60] bg-ink/20 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-103">
        <div className={`absolute top-0 left-0 w-4/5 max-w-sm h-full bg-cream shadow-2xl transition-transform duration-300 ease-out flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`} onClick={e => e.stopPropagation()} data-id="element-104">
          <div className="p-4 border-b border-gold/20 flex items-center justify-between" data-id="element-105">
            <div className="flex items-center space-x-2" data-id="element-106">
              <LogoMark className="w-8 h-8" data-id="element-107" />
              <span className="font-serif text-lg tracking-wide text-gold" data-id="element-108">
                SCULPTED BODY
              </span>
            </div>
            <button onClick={() => setIsMobileMenuOpen(false)} className="text-gold p-2" data-id="element-109">
              <CloseIcon data-id="element-110" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-6 px-6 flex flex-col space-y-6" data-id="element-111">
            {navLinks.map(link => link.hasDropdown ? <div key={link.id} className="flex flex-col" data-id="element-112">
                  <button onClick={() => setMobileAccessoriesOpen(v => !v)} className="text-left text-lg font-serif tracking-wide text-ink hover:text-gold transition-colors flex items-center justify-between" data-id="element-113">
                    <span data-id="element-114">{link.label}</span>
                    <svg className={`w-4 h-4 text-gold transition-transform duration-200 ${mobileAccessoriesOpen ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-115">
                      <path d="M3 4.5L6 7.5L9 4.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-116" />
                    </svg>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${mobileAccessoriesOpen ? 'max-h-40 mt-3' : 'max-h-0'}`} data-id="element-117">
                    <div className="flex flex-col space-y-3 pl-4 border-l border-gold/30" data-id="element-118">
                      <button onClick={() => {
                  setPage('home');
                  setIsMobileMenuOpen(false);
                }} className="text-left text-base font-serif text-ink/80 hover:text-gold transition-colors" data-id="element-119">
                        {t.nav.women}
                      </button>
                      <button onClick={() => {
                  setPage('home');
                  setIsMobileMenuOpen(false);
                }} className="text-left text-base font-serif text-ink/80 hover:text-gold transition-colors" data-id="element-120">
                        {t.nav.men}
                      </button>
                    </div>
                  </div>
                </div> : <button key={link.id} onClick={() => {
            setPage('home');
            setIsMobileMenuOpen(false);
          }} className="text-left text-lg font-serif tracking-wide text-ink hover:text-gold transition-colors" data-id="element-121">
                  {link.label}
                </button>)}
          </div>

          <div className="p-6 border-t border-gold/20 bg-beige" data-id="element-122">
            <div className="flex items-center justify-center space-x-4 text-sm font-medium" data-id="element-123">
              <button onClick={() => setLang('es')} className={`transition-soft ${lang === 'es' ? 'text-gold' : 'text-gold/40'}`} data-id="element-124">
                ESPAÑOL
              </button>
              <span className="text-gold/40" data-id="element-125">|</span>
              <button onClick={() => setLang('en')} className={`transition-soft ${lang === 'en' ? 'text-gold' : 'text-gold/40'}`} data-id="element-126">
                ENGLISH
              </button>
            </div>
          </div>
        </div>
      </div>
    </>;
};